import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { useRole } from '../hooks/useRole';
import { licensePlanService } from '../api';
import { Button, ThemedText, ActionMenu, ConfirmDialog, SkeletonCards } from '../components';
import { extractItems } from '../utils/responseUtils';
import { PageContainer, PageHeaderRow as PageHeader } from '../components/PageStyles';
import { Check, X, Star, Plus } from 'lucide-react';

// --- Helpers ---

const formatBytes = (bytes) => {
  if (bytes == null || bytes === 0) return '0 MB';
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
  return `${(bytes / 1048576).toFixed(0)} MB`;
};

const formatCurrency = (amount) => {
  if (amount == null) return '$0.00';
  return `$${Number(amount).toFixed(2)}`;
};

// --- Styled Components ---

const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${p => p.theme.spacing.lg};
`;

const PlanCard = styled.div`
  position: relative;
  background-color: ${p => p.theme.colors.backgroundSecondary};
  border: ${p => p.$isDefault ? `2px solid ${p.theme.colors.tint}` : `1px solid ${p.theme.colors.border}`};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  display: flex;
  flex-direction: column;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  opacity: ${p => p.$inactive ? 0.6 : 1};

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${p => p.theme.shadows.md};
  }
`;

const DefaultRibbon = styled.div`
  position: absolute;
  top: ${p => p.theme.spacing.md};
  right: ${p => p.theme.spacing.md};
  display: flex;
  align-items: center;
  gap: 4px;
  background-color: ${p => p.theme.colors.tint};
  color: white;
  font-size: ${p => p.theme.typography.sizes.small};
  font-weight: ${p => p.theme.typography.weights.semiBold};
  padding: 3px ${p => p.theme.spacing.sm};
  border-radius: ${p => p.theme.borderRadius.sm};
`;

const PlanName = styled.div`
  font-size: 18px;
  font-weight: ${p => p.theme.typography.weights.bold};
  color: ${p => p.theme.colors.text};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${p => p.theme.spacing.xs};
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
`;

const InactiveBadge = styled.span`
  font-size: ${p => p.theme.typography.sizes.small};
  color: ${p => p.theme.colors.muted};
  text-transform: none;
  letter-spacing: 0;
  font-weight: ${p => p.theme.typography.weights.semiBold};
`;

const PlanCodeChip = styled.code`
  font-size: ${p => p.theme.typography.sizes.small};
  color: ${p => p.theme.colors.primary};
  background-color: ${p => p.theme.colors.primary}12;
  padding: 2px ${p => p.theme.spacing.xs};
  border-radius: ${p => p.theme.borderRadius.sm};
  letter-spacing: 0.3px;
  margin-bottom: ${p => p.theme.spacing.sm};
  display: inline-block;
`;

const PlanDescription = styled.div`
  font-size: ${p => p.theme.typography.sizes.small};
  color: ${p => p.theme.colors.muted};
  margin-bottom: ${p => p.theme.spacing.md};
  line-height: 1.4;
`;

const PriceBlock = styled.div`
  margin-bottom: ${p => p.theme.spacing.md};
  padding-bottom: ${p => p.theme.spacing.md};
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;

const PriceMain = styled.div`
  font-size: 28px;
  font-weight: ${p => p.theme.typography.weights.bold};
  color: ${p => p.theme.colors.primary};
  line-height: 1.2;
`;

const PriceLabel = styled.span`
  font-size: ${p => p.theme.typography.sizes.small};
  font-weight: 400;
  color: ${p => p.theme.colors.muted};
`;

const PriceSecondary = styled.div`
  font-size: ${p => p.theme.typography.sizes.small};
  color: ${p => p.theme.colors.muted};
  margin-top: 2px;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.sm};
  flex: 1;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  font-size: ${p => p.theme.typography.sizes.small};
  color: ${p => p.theme.colors.text};
`;

const FeatureIcon = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const UnlimitedLabel = styled.span`
  font-weight: ${p => p.theme.typography.weights.bold};
  color: ${p => p.theme.colors.tint};
`;

const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  border-top: 1px solid ${p => p.theme.colors.border};
  margin-top: auto;
  padding-top: ${p => p.theme.spacing.md};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${p => p.theme.spacing.xxl} ${p => p.theme.spacing.xl};
  background-color: ${p => p.theme.colors.backgroundSecondary};
  border: 1px dashed ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
`;

// --- Feature Line Builder ---

const getFeatureLines = (plan) => [
  { label: 'Users', value: plan.maxUsers, format: v => v?.toLocaleString() },
  { label: 'Forms', value: plan.maxFormSchemas, format: v => v?.toLocaleString() },
  { label: 'Submissions / mo', value: plan.maxSubmissionsPerMonth, format: v => v?.toLocaleString() },
  { label: 'Storage', value: plan.maxStorageBytes, format: v => formatBytes(v), unlimitedWhenNull: true },
  { label: 'AI requests / mo', value: plan.maxAIGenerationsPerMonth, format: v => v?.toLocaleString() },
  { label: 'Report Templates', value: plan.maxPdfTemplates, format: v => v?.toLocaleString() },
  { label: 'Customers', value: plan.maxCustomers, format: v => v?.toLocaleString() },
  { label: 'Work Orders', value: plan.maxWorkOrders, format: v => v?.toLocaleString() },
  { label: 'Locations', value: plan.maxLocations, format: v => v?.toLocaleString() },
  { label: 'Overage Billing', value: plan.allowOverage ? 'enabled' : null, isBool: true },
];

// --- Component ---

const LicensePlansPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isSuperAdmin } = useRole();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  useEffect(() => { fetchPlans(); }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await licensePlanService.getPlans();
      setPlans(extractItems(response));
    } catch (err) {
      console.error('Failed to fetch license plans:', err);
      toast.error('Failed to load license plans');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await licensePlanService.deletePlan(id);
      toast.success('Plan deleted successfully');
      setDeleteConfirmId(null);
      fetchPlans();
    } catch (err) {
      console.error('Failed to delete plan:', err);
      toast.error(err.response?.data?.message || 'Failed to delete plan');
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await licensePlanService.setDefault(id);
      toast.success('Default plan updated');
      fetchPlans();
    } catch (err) {
      console.error('Failed to set default plan:', err);
      toast.error(err.response?.data?.message || 'Failed to set default plan');
    }
  };

  // --- Access guard ---
  if (!isSuperAdmin()) {
    return (
      <Layout currentPath="/license-plans">
        <PageContainer theme={theme}>
          <ThemedText type="title">Access Denied</ThemedText>
          <ThemedText>You do not have permission to access this page.</ThemedText>
        </PageContainer>
      </Layout>
    );
  }

  const sortedPlans = [...plans].sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));

  return (
    <Layout currentPath="/license-plans">
      <PageContainer theme={theme}>
        <PageHeader theme={theme}>
          <div>
            <ThemedText type="title">License Plans</ThemedText>
            <ThemedText type="subtitle">Manage subscription plans and resource limits</ThemedText>
          </div>
          <Button onClick={() => navigate('/license-plans/new')}>
            <Plus size={16} style={{ marginRight: 4 }} /> Create Plan
          </Button>
        </PageHeader>

        {loading ? (
          <SkeletonCards count={3} theme={theme} />
        ) : plans.length === 0 ? (
          <EmptyState theme={theme}>
            <ThemedText type="subtitle" style={{ marginBottom: 8 }}>No license plans yet</ThemedText>
            <ThemedText style={{ marginBottom: 16 }}>Create your first plan to start managing subscriptions.</ThemedText>
            <Button onClick={() => navigate('/license-plans/new')}>
              <Plus size={16} style={{ marginRight: 4 }} /> Create Plan
            </Button>
          </EmptyState>
        ) : (
          <PlansGrid theme={theme}>
            {sortedPlans.map(plan => (
              <PlanCard key={plan.id} theme={theme} $isDefault={plan.isDefault} $inactive={!plan.isActive}>
                {plan.isDefault && (
                  <DefaultRibbon theme={theme}>
                    <Star size={12} /> Default
                  </DefaultRibbon>
                )}

                <PlanName theme={theme}>
                  {plan.name}
                  {!plan.isActive && <InactiveBadge theme={theme}>Inactive</InactiveBadge>}
                </PlanName>
                <PlanCodeChip theme={theme}>{plan.planCode}</PlanCodeChip>
                {plan.description && (
                  <PlanDescription theme={theme}>{plan.description}</PlanDescription>
                )}

                <PriceBlock theme={theme}>
                  <PriceMain theme={theme}>
                    {formatCurrency(plan.monthlyPrice)} <PriceLabel theme={theme}>/ mo</PriceLabel>
                  </PriceMain>
                  <PriceSecondary theme={theme}>{formatCurrency(plan.annualPrice)} / year</PriceSecondary>
                </PriceBlock>

                <FeatureList theme={theme}>
                  {getFeatureLines(plan).map((feat, idx) => {
                    if (feat.isBool) {
                      return (
                        <FeatureItem key={idx} theme={theme}>
                          <FeatureIcon>
                            {feat.value ? <Check size={14} color={theme.colors.tint} /> : <X size={14} color={theme.colors.muted} />}
                          </FeatureIcon>
                          {feat.label}
                        </FeatureItem>
                      );
                    }
                    const isUnlimited = feat.value == null;
                    return (
                      <FeatureItem key={idx} theme={theme}>
                        <FeatureIcon>
                          {isUnlimited && !feat.unlimitedWhenNull
                            ? <Check size={14} color={theme.colors.tint} />
                            : <Check size={14} color={theme.colors.tint} />}
                        </FeatureIcon>
                        {feat.label}:{' '}
                        {isUnlimited ? (
                          <UnlimitedLabel theme={theme}>&infin;</UnlimitedLabel>
                        ) : (
                          <strong>{feat.format(feat.value)}</strong>
                        )}
                      </FeatureItem>
                    );
                  })}
                </FeatureList>

                <CardActions theme={theme}>
                  <Button size="sm" variant="secondary" onClick={() => navigate(`/license-plans/${plan.id}/edit`)}>
                    Edit
                  </Button>
                  <ActionMenu actions={[
                    ...(!plan.isDefault ? [{ label: 'Set as Default', onClick: () => handleSetDefault(plan.id) }] : []),
                    { label: 'Delete', onClick: () => setDeleteConfirmId(plan.id), variant: 'danger' },
                  ]} />
                </CardActions>
              </PlanCard>
            ))}
          </PlansGrid>
        )}

        <ConfirmDialog
          isOpen={deleteConfirmId !== null}
          title="Delete License Plan"
          message="Are you sure you want to delete this license plan? Organizations using it will keep their current license until reassigned."
          onConfirm={() => handleDelete(deleteConfirmId)}
          onCancel={() => setDeleteConfirmId(null)}
        />
      </PageContainer>
    </Layout>
  );
};

export default LicensePlansPage;
export { LicensePlansPage };
